import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';

const TextareaWithEnhance = forwardRef(({ 
  id, 
  value, 
  onChange, 
  placeholder, 
  rows, 
  maxLength, 
  required, 
  className, 
  onEnhance,
  isApprovalReason
}, ref) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalText, setOriginalText] = useState('');
  const [visibleCharCount, setVisibleCharCount] = useState(0);
  const [lastEnhancedText, setLastEnhancedText] = useState('');
  const [onAcceptCallback, setOnAcceptCallback] = useState(null);
  const [onRejectCallback, setOnRejectCallback] = useState(null);
  
  const textareaRef = useRef(null);
  const animationRef = useRef(null);
  const styleSheetRef = useRef(null);
  const animationCompleteRef = useRef(false);
  
  // 检测用户是否修改了润色后的内容
  useEffect(() => {
    if (!isProcessing && lastEnhancedText && value !== lastEnhancedText) {
      // 用户修改了润色后的内容，重新显示润色按钮
      setLastEnhancedText('');
    }
  }, [value, isProcessing, lastEnhancedText]);

  // 用于从外部接收润色结果的方法
  const startRenderEnhancedText = (enhancedText) => {
    console.log("TextareaWithEnhance开始渲染增强文本:", enhancedText.substring(0, 30) + "...");
    setOriginalText(enhancedText);
    setIsProcessing(true);
  };
  
  // 添加用于从外部触发增强动画的方法
  const startEnhancingAnimation = () => {
    setIsProcessing(true);
  };
  
  // 新增：隐藏处理中提示的方法
  const hideEnhancingPlaceholder = () => {
    setIsProcessing(false);
  };
  
  // 新增：带确认按钮的润色文本渲染 - 修改为自动接受
  const startRenderEnhancedTextWithConfirmation = (enhancedText, onAccept, onReject) => {
    console.log("TextareaWithEnhance开始渲染增强文本，将自动接受");
    setOriginalText(enhancedText);
    setOnAcceptCallback(() => onAccept);
    setOnRejectCallback(() => onReject);
    setIsProcessing(true);
  };
  
  // 新增：恢复原始文本的方法
  const restoreOriginalText = (text) => {
    // 直接恢复原始文本，不进行动画
    if (onChange) {
      onChange({ target: { value: text } });
    }
    setIsProcessing(false);
  };
  
  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    startRenderEnhancedText,
    startEnhancingAnimation,
    hideEnhancingPlaceholder,
    startRenderEnhancedTextWithConfirmation,
    restoreOriginalText
  }));
  
  // 处理文本输出动画
  useEffect(() => {
    if (isProcessing && originalText) {
      console.log("开始文本动画效果, 文本长度:", originalText.length);
      
      // 先确保清理之前可能存在的样式表
      if (styleSheetRef.current && document.head.contains(styleSheetRef.current)) {
        try {
          document.head.removeChild(styleSheetRef.current);
        } catch (e) {
          console.log('Style element already removed');
        }
      }
      
      // 添加动画样式到文档 - 使用独立的keyframes名称避免冲突
      const styleSheet = document.createElement('style');
      styleSheet.setAttribute('id', 'text-animation-style-' + Date.now());
      styleSheet.textContent = `
        @keyframes textFadeIn {
          0% { 
            opacity: 0; 
            transform: translateY(8px);
            filter: blur(4px);
          }
          60% {
            opacity: 0.6; 
            transform: translateY(3px);
            filter: blur(1px);
          }
          100% { 
            opacity: 1; 
            transform: translateY(0);
            filter: blur(0);
          }
        }
        
        @-webkit-keyframes textFadeIn {
          0% { 
            opacity: 0; 
            -webkit-transform: translateY(8px);
            -webkit-filter: blur(4px);
          }
          60% {
            opacity: 0.6; 
            -webkit-transform: translateY(3px);
            -webkit-filter: blur(1px);
          }
          100% { 
            opacity: 1; 
            -webkit-transform: translateY(0);
            -webkit-filter: blur(0);
          }
        }

        .typing-container span {
          display: inline-block !important;
          opacity: 0 !important;
        }

        .typing-container span.animated {
          animation: textFadeIn 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
          -webkit-animation: textFadeIn 0.8s cubic-bezier(0.19, 1, 0.22, 1) forwards;
        }
      `;
      document.head.appendChild(styleSheet);
      styleSheetRef.current = styleSheet;
      
      // 清空内容
      onChange({ target: { value: '' } });
      setVisibleCharCount(0);
      animationCompleteRef.current = false;
      
      // 使用requestAnimationFrame实现流畅动画
      let lastTimestamp = 0;
      let charIndex = 0;
      
      const animate = (timestamp) => {
        if (!lastTimestamp) lastTimestamp = timestamp;
        
        // 计算经过的时间
        const elapsed = timestamp - lastTimestamp;
        
        // 每8毫秒添加一个字符，更接近ChatGPT的效果
        if (elapsed >= 8 && charIndex < originalText.length) {
          // 更新可见字符数量
          charIndex++;
          setVisibleCharCount(charIndex);
          
          // 同步更新textarea的值
          onChange({ target: { value: originalText.substring(0, charIndex) } });
          
          // 重置时间戳
          lastTimestamp = timestamp;
        }
        
        // 继续动画直到所有字符都显示
        if (charIndex < originalText.length) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // 标记动画已完成
          animationCompleteRef.current = true;
          
          // 确保最终文本是完整的
          onChange({ target: { value: originalText } });
          
          // 动画完成后的清理
          setTimeout(() => {
            setIsProcessing(false);
            
            // 保存润色后的文本，用于比较用户是否修改
            setLastEnhancedText(originalText);
            
            // 自动接受润色结果
            if (onAcceptCallback) {
              onAcceptCallback();
            }
            
            // 重置回调
            setOnAcceptCallback(null);
            setOnRejectCallback(null);
            
            // 重要：最后再次确保文本是完整的
            onChange({ target: { value: originalText } });
          }, 600); // 缩短结束时间，提高响应速度
        }
      };
      
      // 开始动画
      setTimeout(() => {
        animationRef.current = requestAnimationFrame(animate);
      }, 30); // 缩短延迟时间
    }
    
    return () => {
      // 清理动画
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      // 如果动画已完成但组件要卸载，确保文本是完整的
      if (isProcessing && originalText) {
        onChange({ target: { value: originalText } });
      }
      
      // 安全地清理样式表
      if (styleSheetRef.current) {
        try {
          if (document.head.contains(styleSheetRef.current)) {
            document.head.removeChild(styleSheetRef.current);
          }
        } catch (e) {
          console.log('Style cleanup error:', e);
        }
        styleSheetRef.current = null;
      }
    };
  }, [isProcessing, originalText, onChange, onAcceptCallback]);
  
  // 组件卸载时的清理工作
  useEffect(() => {
    return () => {
      // 确保在组件卸载时清理所有资源
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      
      if (styleSheetRef.current) {
        try {
          if (document.head.contains(styleSheetRef.current)) {
            document.head.removeChild(styleSheetRef.current);
          }
        } catch (e) {
          console.log('Final cleanup error:', e);
        }
        styleSheetRef.current = null;
      }
    };
  }, []);
  
  return (
    <div className="textarea-container" style={{ position: 'relative' }}>
      <textarea
        ref={textareaRef}
        id={id}
        value={value}
        onChange={onChange}
        placeholder={placeholder || "输入您的内容..."}
        rows={rows || 4}
        maxLength={maxLength}
        required={required}
        className={`${className || "elegant-textarea"} ${isApprovalReason ? "approval-reason-textarea" : ""}`}
        disabled={isProcessing}
        style={{
          width: '100%',
          padding: '14px 16px',
          background: '#f9f9f9',
          border: '1px solid #eee',
          borderRadius: '15px',
          fontSize: '1rem',
          color: '#333',
          resize: 'vertical',
          transition: 'all 0.3s ease',
          fontFamily: '"Microsoft YaHei", "微软雅黑", sans-serif',
          ...(isApprovalReason && { height: `calc(${rows || 4}em + 83px)` })
        }}
      />
      
      {isProcessing && (
        <div className="processing-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(249, 249, 249, 0.9)',
          borderRadius: '15px',
          backdropFilter: 'blur(3px)',
          zIndex: 5
        }}>
          <div className="enhancing-animation" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            <div style={{
              fontWeight: 'bold',
              fontSize: '16px',
              color: 'transparent',
              background: 'linear-gradient(120deg, #4a90e2 0%, #ff9db0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              animation: 'pulse 1.5s infinite',
              textAlign: 'center'
            }}>
              正在使用 AI 进行文本润色中
            </div>
            <div style={{
              fontSize: '12px',
              color: '#888',
              marginTop: '8px',
              textAlign: 'center'
            }}>
              需要一些时间才能完成，请耐心等待
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        textarea:focus:not(.approval-reason-textarea) {
          background: white;
          border-color: #fd9267;
          box-shadow: 0 0 0 3px rgba(253, 146, 103, 0.1);
          outline: none;
        }
        .approval-reason-textarea:focus {
          background: #f9f9f9;
          border-color: #eee;
          box-shadow: none;
          outline: none;
        }
        textarea::placeholder {
          color: #aaa;
        }
      `}</style>
    </div>
  );
});

export default TextareaWithEnhance; 